import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import NiceModal from "@ebay/nice-modal-react";
import { Table } from "../../../components/table/Table";
import { TableErrorWrapper } from "../../../components/table/TableErrorWrapper";
import { RT8Pagination } from "../../../components/table/Pagination";
import { SearchInput } from "../../../components/fields/base/TextField";
import { css } from "../../../../styled-system/css";
import { Flex, HStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { ColorSwatch } from "../../../components/fields/base/ColorSwatch";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { FaPencil, FaTrash } from "react-icons/fa6";
import {
  TAG_SCOPE_LABELS,
  TAG_VISIBILITY_LABELS,
  type Tag,
  type TagEntityType,
} from "../../../zodios/apiDefinitions/api/tags";
import { useDeleteTagMutation } from "../api/useDeleteTagMutation";
import { useTagsQuery } from "../api/useTagsQuery";
import { TagFormModal } from "./TagFormModal";
import { useSimpleCan } from "../../../config/casl/casl";

const columnHelper = createAppColumnHelper<Tag>();

const EMPTY_TAGS: Tag[] = [];

interface TagTableProps {
  entityType: TagEntityType;
  tagType: "user" | "system";
}

export function TagTable({ entityType, tagType }: TagTableProps) {
  const { t } = useTranslation(["tags"]);
  const isUserTags = tagType === "user";
  const tagsQuery = useTagsQuery({ entityType, type: isUserTags ? "user" : undefined });
  const tags = useMemo(() => {
    const results = tagsQuery.data?.results ?? EMPTY_TAGS;
    return isUserTags ? results : results.filter((tag) => tag.type !== "user");
  }, [tagsQuery.data, isUserTags]);

  const { mutate: deleteTag } = useDeleteTagMutation();
  const userCanUpdateTags = useSimpleCan("update", "Tag");
  const userCanDeleteTags = useSimpleCan("delete", "Tag");

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: t(($) => $.tags.table["Name"]),
        size: 300,
        cell: (props) => {
          const { color, description } = props.row.original;
          return (
            <Flex gap="xsmall" direction="column">
              <Flex gap="small" alignItems="center">
                <ColorSwatch size="small" color={color} />
                <span className={css({ fontWeight: "500" })}>{props.getValue()}</span>
              </Flex>
              {description && (
                <span className={css({ color: "text.secondary", fontSize: "small", lineHeight: "tight" })}>
                  {description}
                </span>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.accessor((row) => row.scope_definition.scope, {
        id: "scope",
        header: t(($) => $.tags.table["Scope"]),
        size: 120,
        cell: (props) => {
          const scope = props.getValue();
          return <span>{t(($) => $.tags.scope[TAG_SCOPE_LABELS[scope]])}</span>;
        },
      }),
      columnHelper.accessor((row) => row.scope_definition.visibility, {
        id: "visibility",
        header: t(($) => $.tags.table["Visibility"]),
        size: 120,
        cell: (props) => {
          const visibility = props.getValue();
          return <span>{t(($) => $.tags.visibility[TAG_VISIBILITY_LABELS[visibility]])}</span>;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: t(($) => $.tags.table["Actions"]),
        size: 120,
        cell: (props) => {
          const tag = props.row.original;
          return (
            <HStack gap="small">
              <Button
                variant="contentOnly"
                onPress={() => void NiceModal.show(TagFormModal, { entityType, initialTag: tag })}
                aria-label={t(($) => $.tags.table["Edit tag"])}
                isDisabled={!userCanUpdateTags}
                className={css({
                  padding: "xsmall",
                  color: "grey",
                  _hover: { color: "trellisPalette.3" },
                })}
              >
                <FaPencil />
              </Button>
              {isUserTags && (
                <ConfirmationModal
                  title={t(($) => $.tags.table["Delete Tag"])}
                  confirmationMessage={t(
                    ($) =>
                      $.tags.table['Are you sure you want to delete the tag "{name}"? This action cannot be undone.'],
                    { name: tag.name },
                  )}
                  confirmButtonText={t(($) => $.tags.table["Delete"])}
                  modalButtonText={<FaTrash />}
                  modalButtonProps={{
                    variant: "contentOnly",
                    isDisabled: !userCanDeleteTags,
                    className: css({
                      padding: "xsmall",
                      color: "grey",
                      _hover: { color: "error" },
                    }),
                  }}
                  onConfirm={() => {
                    deleteTag(tag.id);
                  }}
                />
              )}
            </HStack>
          );
        },
      }),
    ];
  }, [isUserTags, entityType, deleteTag, userCanUpdateTags, userCanDeleteTags, t]);

  const table = useClientTable({
    data: tags,
    columns,
    enableGlobalFilter: true,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      showSkeleton: tagsQuery.isFetching && (!tagsQuery.data || tagsQuery.isPlaceholderData),
    },
  });

  return (
    <TableErrorWrapper error={tagsQuery.error} userMessage={t(($) => $.tags.table["Failed to load tags"])}>
      {!tagsQuery.isLoading && tags.length === 0 ?
        <div className={css({ padding: "large", color: "text.secondary", textAlign: "center" })}>
          {t(($) => $.tags.table["No tags found for this category."])}
        </div>
      : <div>
          <HStack justifyContent="space-between" paddingBottom="small">
            <SearchInput
              aria-label={t(($) => $.tags.table["Search tags"])}
              placeholder={t(($) => $.tags.table["Search tags..."])}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
              css={{ maxWidth: "300px" }}
            />
            <RT8Pagination table={table} />
          </HStack>
          <Table table={table} />
        </div>
      }
    </TableErrorWrapper>
  );
}
